import { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton, Tooltip, Snackbar, Alert } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { api } from "../services/api";
import { Link } from "react-router-dom";

const SOURCE_MAX_LENGTH = 50;

const DEFAULT_SOURCE_EDIT = {
  id: null,
  name: "",
  email: "",
  source: "",
};

const DEFAULT_PACK_GRANT = {
  id: null,
  name: "",
  email: "",
  source: "",
};

const DEFAULT_NEW_USER = {
  first_name: "",
  last_name: "",
  email: "",
  team_name: "",
  country: "",
  gender: "",
  source: "undelta",
  language_preference: "es",
};

const DEFAULT_DELETE_USER = {
  id: null,
  name: "",
  email: "",
  step: 1,
  confirmationEmail: "",
};

const DEFAULT_EMAIL_VERIFICATION = {
  id: null,
  name: "",
  email: "",
  email_verified: false,
};

const inputStyle = {
  padding: "6px 10px",
  backgroundColor: "#222",
  color: "#fff",
  border: "1px solid #7e57c2",
  borderRadius: "4px",
};

const formatDate = (dateValue) => new Date(dateValue).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

const formatTime = (dateValue) => new Date(dateValue).toLocaleTimeString("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [saving, setSaving] = useState(false);
  const [grantingPack, setGrantingPack] = useState(false);
  const [sourceEdit, setSourceEdit] = useState(DEFAULT_SOURCE_EDIT);
  const [packGrant, setPackGrant] = useState(DEFAULT_PACK_GRANT);
  const [newUser, setNewUser] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [creationMessage, setCreationMessage] = useState("");
  const [creationMessageSeverity, setCreationMessageSeverity] = useState("success");
  const [resendingActivationId, setResendingActivationId] = useState(null);
  const [deleteUser, setDeleteUser] = useState(DEFAULT_DELETE_USER);
  const [deletingUser, setDeletingUser] = useState(false);
  const [emailVerification, setEmailVerification] = useState(DEFAULT_EMAIL_VERIFICATION);
  const [savingEmailVerification, setSavingEmailVerification] = useState(false);
  const limit = 30;

  const fetchData = async (pageToLoad = 1, searchOverride = search) => {
    const query = new URLSearchParams({
      page: pageToLoad,
      limit,
      ...(statusFilter && { status: statusFilter }),
      ...(searchOverride && { q: searchOverride }),
      sort_by: sortBy,
      sort_dir: sortDir,
    }).toString();

    const res = await api.get(`/api/admin/users/list?${query}`);
    setUsers(res.data.users);
    setPage(res.data.page);
    setTotal(res.data.total);
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, sortBy, sortDir]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const handleToggleCanOperate = async (userId, newValue) => {
    await api.patch(`/api/admin/users/${userId}`, { can_operate: newValue });
    fetchData(page);
  };

  const handleStatusChange = async (userId, newStatus) => {
    await api.patch(`/api/admin/users/${userId}`, { status: newStatus });
    fetchData(page);
  };

  const handleSourceSave = async () => {
    try {
      setSaving(true);
      await api.patch(`/api/admin/users/${sourceEdit.id}`, {
        source: (sourceEdit.source || "").trim().toLowerCase(),
      });
      setSourceEdit(DEFAULT_SOURCE_EDIT);
      fetchData(page);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error updating source");
    } finally {
      setSaving(false);
    }
  };

  const handleGrantCarpinchiaPack = async () => {
    try {
      setGrantingPack(true);
      await api.post(`/api/admin/users/${packGrant.id}/grant-carpinchia-pack`);
      setPackGrant(DEFAULT_PACK_GRANT);
      fetchData(page);
      alert("CarpinchIA Additional Sessions assigned successfully.");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error assigning CarpinchIA pack");
    } finally {
      setGrantingPack(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setCreatingUser(true);
      await api.post("/api/admin/users", newUser);
      setNewUser(null);
      setSearch("");
      fetchData(1, "");
      setCreationMessageSeverity("success");
      setCreationMessage("User created successfully. The activation email was sent.");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error creating user");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleResendActivation = async (userId) => {
    try {
      setResendingActivationId(userId);
      await api.post(`/api/admin/users/${userId}/resend-activation`);
      setCreationMessageSeverity("success");
      setCreationMessage("Activation email resent successfully.");
    } catch (err) {
      console.error(err);
      setCreationMessageSeverity("error");
      setCreationMessage(err.response?.data?.error || "Activation email could not be sent.");
    } finally {
      setResendingActivationId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (deleteUser.step === 1) {
      setDeleteUser({ ...deleteUser, step: 2 });
      return;
    }

    try {
      setDeletingUser(true);
      await api.delete(`/api/admin/users/${deleteUser.id}`);
      setDeleteUser(DEFAULT_DELETE_USER);
      setCreationMessageSeverity("success");
      setCreationMessage("User and all related data deleted successfully.");
      fetchData(page, search);
    } catch (err) {
      console.error(err);
      setCreationMessageSeverity("error");
      setCreationMessage(err.response?.data?.error || "User could not be deleted.");
    } finally {
      setDeletingUser(false);
    }
  };

  const handleEmailVerificationChange = async () => {
    const nextValue = !emailVerification.email_verified;
    try {
      setSavingEmailVerification(true);
      await api.patch(`/api/admin/users/${emailVerification.id}`, {
        email_verified: nextValue,
      });
      setEmailVerification(DEFAULT_EMAIL_VERIFICATION);
      fetchData(page, search);
    } catch (err) {
      console.error(err);
      setCreationMessageSeverity("error");
      setCreationMessage(err.response?.data?.error || "Email verification could not be updated.");
    } finally {
      setSavingEmailVerification(false);
    }
  };

  const renderModal = (title, content) => (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          backgroundColor: "#452654",
          borderRadius: 2,
          p: 4,
          minWidth: 420,
          maxWidth: "92vw",
          boxShadow: 6,
        }}
      >
        <Typography variant="body2" sx={{ mb: 2, fontWeight: "bold", color: "#fff" }}>
          {title}
        </Typography>
        {content}
      </Box>
    </Box>
  );

  const totalPages = Math.ceil(total / limit);

  const columns = [
    { label: "Date", field: "created_at" },
    { label: "Name / Email", field: "first_name" },
    { label: "Sessions", field: "sessions_count", sortable: false },
    { label: "Country", field: "country" },
    { label: "Source", field: "source" },
    { label: "Can Operate", field: "can_operate" },
    { label: "Status", field: "status" },
    { label: "Last Login", field: "last_login_date" },
    { label: "Actions", field: "actions", sortable: false },
  ];

  return (
    <Box sx={{
      backgroundImage: 'url("/assets/bg-pitchest.jpg")',
      backgroundSize: "cover",
      minHeight: "100vh",
      px: 2,
      py: 6,
    }}>
      <Box
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderRadius: 4,
          padding: { xs: 2, md: 4 },
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5">👤 Users</Typography>
          <Button variant="contained" onClick={() => setNewUser(DEFAULT_NEW_USER)}>
            Add User
          </Button>
          <Link to="/admin" style={{ color: "#90caf9", fontWeight: "bold" }}>⬅ Back</Link>
        </Box>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Total: {total} — Page {page} of {totalPages}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <input
            type="text"
            name="user-list-filter"
            autoComplete="new-password"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                fetchData(1);
              }
            }}
            placeholder="Search name, email..."
            style={inputStyle}
          />
          <Button
            variant="outlined"
            onClick={() => fetchData(1)}
            size="small"
            sx={{ ml: 1 }}
          >
            🔍 Search
          </Button>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="banned">Banned</option>
          </select>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", color: "white" }}>
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    onClick={() => col.sortable !== false && handleSort(col.field)}
                    style={{
                      padding: "8px",
                      backgroundColor: "#34163A",
                      cursor: col.sortable === false ? "default" : "pointer",
                    }}
                  >
                    {col.label} {col.sortable !== false && sortBy === col.field && (sortDir === "asc" ? "▲" : "▼")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr key={u.id} style={{ backgroundColor: idx % 2 === 0 ? "#5E2F6E" : "transparent" }}>
                  <td style={{ padding: "8px", whiteSpace: "nowrap" }}>
                    <div>{formatDate(u.created_at)}</div>
                    <div style={{ color: "#c7a9d9", fontSize: "0.78rem", marginTop: "2px" }}>
                      {formatTime(u.created_at)}
                    </div>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <div>{u.first_name} {u.last_name}</div>
                    <div style={{ color: "#c7a9d9", fontSize: "0.8rem", marginTop: "3px" }}>
                      <Tooltip title={u.email_verified ? "Email verified" : "Email not verified"}>
                        <IconButton
                          size="small"
                          sx={{ p: 0, mr: 0.5, verticalAlign: "middle" }}
                          onClick={() => setEmailVerification({
                            id: u.id,
                            name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
                            email: u.email || "",
                            email_verified: Boolean(u.email_verified),
                          })}
                        >
                          {u.email_verified ? (
                            <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 16 }} />
                          ) : (
                            <CancelIcon sx={{ color: "#ef5350", fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      {u.email}
                    </div>
                    {u.team_name && (
                      <div style={{ color: "#c7a9d9", fontSize: "0.8rem", marginTop: "3px" }}>
                        Team: {u.team_name}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "8px", whiteSpace: "nowrap" }}>
                    {u.sessions_count > 0 ? (
                      <Link
                        to={`/admin/sessions?q=${encodeURIComponent(u.email)}`}
                        style={{ color: "#fff", fontWeight: "bold", textDecoration: "underline" }}
                      >
                        {u.sessions_count} Used
                      </Link>
                    ) : (
                      <div>{u.sessions_count} Used</div>
                    )}
                    <div
                      style={{
                        color: u.available_sessions > 0 ? "#81c784" : "#ef9a9a",
                        marginTop: "3px",
                      }}
                    >
                      {u.available_sessions} Left
                    </div>
                    {u.active_plan_end_date && (
                      <div style={{ color: "#c7a9d9", fontSize: "0.78rem", marginTop: "3px" }}>
                        Plan expires: {formatDate(u.active_plan_end_date)}
                      </div>
                    )}
                    {!u.active_plan_end_date && (
                      <div style={{ color: "#c7a9d9", fontSize: "0.78rem", marginTop: "3px" }}>
                        No active plan
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "8px" }}>{u.country}</td>
                  <td style={{ padding: "8px", whiteSpace: "nowrap" }}>
                    <span>{u.source || "-"}</span>
                    <Tooltip title="Edit source">
                      <IconButton
                        size="small"
                        sx={{ color: "#90caf9", ml: 1 }}
                        onClick={() => setSourceEdit({
                          id: u.id,
                          name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
                          email: u.email || "",
                          source: u.source || "",
                        })}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Tooltip title={u.email_verified ? "Toggle Can Operate" : "Email must be verified first"}>
                      <span
                        onClick={() => u.email_verified && handleToggleCanOperate(u.id, !u.can_operate)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          cursor: u.email_verified ? "pointer" : "not-allowed",
                          backgroundColor: u.email_verified
                            ? (u.can_operate ? "#1b5e20" : "#888")
                            : "#555",
                          color: u.email_verified ? "white" : "#aaa",
                          fontWeight: "bold",
                          opacity: u.email_verified ? 1 : 0.7,
                        }}
                      >
                        {u.can_operate ? "YES" : "NO"}
                      </span>
                    </Tooltip>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <select
                      value={u.status}
                      onChange={(e) => handleStatusChange(u.id, e.target.value)}
                      style={{
                        backgroundColor:
                          u.status === "new" ? "#263bf3ff" :
                          u.status === "active" ? "#1b5e20" :
                          u.status === "banned" ? "#b71c1c" :
                          "#888",
                        color: "white",
                        border: "1px solid #888",
                        borderRadius: "4px",
                        padding: "4px 8px"
                      }}
                    >
                      <option value="new">-New-</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="banned">Banned</option>
                    </select>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <div style={{ color: "#c7a9d9", fontSize: "0.78rem" }}>
                      {u.last_login_date ? new Date(u.last_login_date).toLocaleString() : ""}
                    </div>
                    {u.last_login_device_type && <div style={{ marginTop: "3px" }}>{u.last_login_device_type}</div>}
                    {u.last_login_ip && <div>{u.last_login_ip}</div>}
                  </td>
                  <td style={{ padding: "8px", whiteSpace: "nowrap" }}>
                    {u.source === "undelta" && (
                      <Tooltip title="Add CarpinchIA pack">
                        <IconButton
                          size="small"
                          sx={{ color: "#FBBF24" }}
                          onClick={() => setPackGrant({
                            id: u.id,
                            name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
                            email: u.email || "",
                            source: u.source || "",
                          })}
                        >
                          <AddCircleOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {u.activation_pending && (
                      <Tooltip title="Resend activation email">
                        <span>
                          <IconButton
                            size="small"
                            sx={{ color: "#90caf9" }}
                            disabled={resendingActivationId === u.id}
                            onClick={() => handleResendActivation(u.id)}
                          >
                            <EmailOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete user and all related data">
                      <IconButton
                        size="small"
                        sx={{ color: "#ef5350" }}
                        onClick={() => setDeleteUser({
                          id: u.id,
                          name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
                          email: u.email || "",
                          step: 1,
                          confirmationEmail: "",
                        })}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: "16px", textAlign: "center", backgroundColor: "#452654" }}>
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="outlined" disabled={page === 1} onClick={() => fetchData(page - 1)}>⬅ Prev</Button>
          <Button variant="outlined" disabled={page === totalPages} onClick={() => fetchData(page + 1)}>Next ➡</Button>
        </Box>
      </Box>

      {sourceEdit.id && renderModal(
        "Edit User Source",
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
            <Typography variant="body2" sx={{ color: "#ccc" }}>
              {sourceEdit.name || "-"} — {sourceEdit.email || "-"}
            </Typography>
            <input
              type="text"
              maxLength={SOURCE_MAX_LENGTH}
              value={sourceEdit.source}
              onChange={(e) => setSourceEdit({
                ...sourceEdit,
                source: e.target.value.toLowerCase(),
              })}
              placeholder="source"
              style={inputStyle}
            />
            <Typography variant="caption" sx={{ color: "#bbb" }}>
              Max {SOURCE_MAX_LENGTH} characters. Saved in lowercase.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button variant="contained" disabled={saving} onClick={handleSourceSave}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={saving}
              onClick={() => setSourceEdit(DEFAULT_SOURCE_EDIT)}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}

      {newUser && renderModal(
        "Add New User And Send Activation Email",
        <>
          <Typography variant="body2" sx={{ color: "#ccc", mb: 2 }}>
            This user will be created with <b>UNDelta</b> access and will receive an account activation email.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
            <input
              type="text"
              placeholder="First name"
              value={newUser.first_name}
              onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Last name"
              value={newUser.last_name}
              onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
              style={inputStyle}
            />
            <input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              style={inputStyle}
            />
            <input
              type="text"
              maxLength={150}
              placeholder="Team or startup name"
              value={newUser.team_name}
              onChange={(e) => setNewUser({ ...newUser, team_name: e.target.value })}
              style={inputStyle}
            />
            <input
              type="text"
              maxLength={100}
              placeholder="Country (optional)"
              value={newUser.country}
              onChange={(e) => setNewUser({ ...newUser, country: e.target.value })}
              style={inputStyle}
            />
            <select
              value={newUser.gender}
              onChange={(e) => setNewUser({ ...newUser, gender: e.target.value })}
              style={inputStyle}
            >
              <option value="">Gender (optional)</option>
              <option value="m">Male</option>
              <option value="f">Female</option>
              <option value="u">Undefined</option>
            </select>
            <select
              value={newUser.source}
              onChange={(e) => setNewUser({ ...newUser, source: e.target.value })}
              style={inputStyle}
            >
              <option value="undelta">UNDelta</option>
            </select>
            <select
              value={newUser.language_preference}
              onChange={(e) => setNewUser({ ...newUser, language_preference: e.target.value })}
              style={inputStyle}
            >
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </Box>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disabled={creatingUser || !newUser.first_name.trim() || !newUser.last_name.trim() || !newUser.email.trim()}
              onClick={handleCreateUser}
            >
              {creatingUser ? "Creating..." : "Create User"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={creatingUser}
              onClick={() => setNewUser(null)}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}

      {packGrant.id && renderModal(
        "Add CarpinchIA Pack",
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
            <Typography variant="body2" sx={{ color: "#ccc" }}>
              <strong>User:</strong> {packGrant.name || "-"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#ccc" }}>
              <strong>Email:</strong> {packGrant.email || "-"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#ccc" }}>
              <strong>Source:</strong> {packGrant.source || "-"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#fff", mt: 1 }}>
              <strong>Pack:</strong> CarpinchIA Additional Sessions
            </Typography>
            <Typography variant="body2" sx={{ color: "#fff" }}>
              <strong>Sessions:</strong> 10
            </Typography>
            <Typography variant="caption" sx={{ color: "#bbb", mt: 1 }}>
              This creates an internal zero-cost purchase, payment and session pack for this UNDelta user.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button variant="contained" disabled={grantingPack} onClick={handleGrantCarpinchiaPack}>
              {grantingPack ? "Assigning..." : "Confirm"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={grantingPack}
              onClick={() => setPackGrant(DEFAULT_PACK_GRANT)}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}

      {deleteUser.id && renderModal(
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#ffb74d" }}>
          <WarningAmberIcon />
          <span>{deleteUser.step === 1 ? "Delete User" : "Confirm Permanent Deletion"}</span>
        </Box>,
        <>
          <Typography variant="body2" sx={{ color: "#fff", mb: 2 }}>
            <strong>{deleteUser.name || "-"}</strong>
            <br />
            {deleteUser.email}
          </Typography>
          {deleteUser.step === 1 ? (
            <>
              <Typography variant="body2" sx={{ color: "#ffb4ab", mb: 3 }}>
                This permanently deletes the user and all related data:<br/>
                - sessions & evaluations,<br/>
                - purchases, payments, packs,<br/>
                - settings and applications.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button variant="contained" color="error" onClick={handleDeleteUser}>
                  Continue
                </Button>
                <Button variant="outlined" onClick={() => setDeleteUser(DEFAULT_DELETE_USER)}>
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: "#ffb4ab", mb: 2 }}>
                To confirm, type the user email exactly:
              </Typography>
              <input
                type="email"
                autoComplete="off"
                value={deleteUser.confirmationEmail}
                onChange={(e) => setDeleteUser({ ...deleteUser, confirmationEmail: e.target.value })}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: 20 }}
              />
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  color="error"
                  disabled={deletingUser || deleteUser.confirmationEmail !== deleteUser.email}
                  onClick={handleDeleteUser}
                >
                  {deletingUser ? "Deleting..." : "Delete Permanently"}
                </Button>
                <Button
                  variant="outlined"
                  disabled={deletingUser}
                  onClick={() => setDeleteUser(DEFAULT_DELETE_USER)}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {emailVerification.id && renderModal(
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#ffb74d" }}>
          <WarningAmberIcon />
          <span>Change Email Verification</span>
        </Box>,
        <>
          <Typography variant="body2" sx={{ color: "#fff", mb: 1 }}>
            <strong>{emailVerification.name || "-"}</strong>
            <br />
            {emailVerification.email}
          </Typography>
          <Typography variant="body2" sx={{ color: "#ddd", mb: 1 }}>
            Current Value:{" "}
            <strong style={{ color: emailVerification.email_verified ? "#66bb6a" : "#ef5350" }}>
              {emailVerification.email_verified ? "TRUE" : "FALSE"}
            </strong>
          </Typography>
          <Typography variant="body2" sx={{ color: "#ffb74d", mb: 3 }}>
            Click Confirm to set Email Verified to{" "}
            <strong style={{ color: emailVerification.email_verified ? "#ef5350" : "#66bb6a" }}>
              {emailVerification.email_verified ? "FALSE" : "TRUE"}
            </strong>
            .
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disabled={savingEmailVerification}
              onClick={handleEmailVerificationChange}
            >
              {savingEmailVerification ? "Saving..." : "Confirm"}
            </Button>
            <Button
              variant="outlined"
              disabled={savingEmailVerification}
              onClick={() => setEmailVerification(DEFAULT_EMAIL_VERIFICATION)}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={Boolean(creationMessage)}
        autoHideDuration={5000}
        onClose={() => setCreationMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert onClose={() => setCreationMessage("")} severity={creationMessageSeverity} variant="filled" sx={{ width: "100%" }}>
          {creationMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;
